/*
 * Shared run-lifecycle status section for Deckorate's AI image feature
 * panels (Sketch to Slide, Photo Headshot, ...) — the phased progress list
 * + Cancel button while running, and the done/cancelled/error banners once
 * the run settles. Presentation-agnostic like ProgressSteps.tsx: callers
 * pass their own CSS module's class names so each panel's existing look is
 * unchanged.
 */

import type { ReactNode } from "react";
import type { ProgressStepsClassNames } from "./ProgressSteps";
import { ProgressSteps } from "./ProgressSteps";

export type RunLifecycleStatus = "idle" | "running" | "done" | "cancelled" | "error";

export interface RunStatusSectionProps<TPhase extends string> {
  readonly status: RunLifecycleStatus;
  /** Required (and only used) when `status` is "running". */
  readonly phase?: TPhase;
  readonly phases: ReadonlyArray<{ readonly phase: TPhase; readonly label: string }>;
  readonly progressAriaLabel: string;
  /** Required (and only used) when `status` is "done". */
  readonly doneMessage?: ReactNode;
  /** Required (and only used) when `status` is "error". */
  readonly errorMessage?: string;
  readonly onCancel: () => void;
  readonly onDismiss: () => void;
  readonly classNames: ProgressStepsClassNames;
}

export function RunStatusSection<TPhase extends string>({
  status,
  phase,
  phases,
  progressAriaLabel,
  doneMessage,
  errorMessage,
  onCancel,
  onDismiss,
  classNames,
}: Readonly<RunStatusSectionProps<TPhase>>) {
  if (status === "running" && phase !== undefined) {
    return (
      <>
        <ProgressSteps phases={phases} current={phase} ariaLabel={progressAriaLabel} classNames={classNames} />
        <button type="button" className={classNames.cancelBtn} onClick={onCancel}>
          Cancel
        </button>
      </>
    );
  }

  if (status === "done") {
    return (
      <div className={`${classNames.statusBox} ${classNames.statusSuccess}`}>
        <span className={classNames.statusText}>{doneMessage}</span>
        <button type="button" className={classNames.revertBtn} onClick={onDismiss}>
          Dismiss
        </button>
      </div>
    );
  }

  if (status === "cancelled") {
    return (
      <div className={`${classNames.statusBox} ${classNames.statusInfo}`}>
        <span className={classNames.statusText}>Cancelled — nothing was changed.</span>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className={`${classNames.statusBox} ${classNames.statusError}`}>
        <span className={classNames.statusLabel}>Failed:</span>
        <span className={classNames.statusText}>{errorMessage}</span>
      </div>
    );
  }

  return null;
}
